import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api, HQ_TOKEN_KEY, TOKEN_KEY } from "@/lib/api";
import { resetSetupCache } from "@/hooks/useSetup";
import { BootScreen } from "@/components/flow/BootScreen";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(!!token);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setLoading(false);
      return;
    }
    try {
      const [me, m] = await Promise.all([api.get("/auth/me"), api.get("/meta")]);
      setUser(me.data.user);
      setOrg(me.data.organization);
      setMeta(m.data);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
      setOrg(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    resetSetupCache();
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    setOrg(r.data.organization);
    try {
      const m = await api.get("/meta");
      setMeta(m.data);
    } catch (e) {
      /* ignore */
    }
    return r.data;
  }, []);

  const register = useCallback(async (payload) => {
    const r = await api.post("/auth/register", payload);
    resetSetupCache();
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    setOrg(r.data.organization);
    return r.data;
  }, []);

  const logout = useCallback(() => {
    resetSetupCache();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(HQ_TOKEN_KEY);
    setToken(null);
    setUser(null);
    setOrg(null);
  }, []);

  /** Sign in with a session returned by another auth endpoint (e.g. set/reset password). */
  const adoptSession = useCallback(async (data) => {
    resetSetupCache();
    localStorage.removeItem(HQ_TOKEN_KEY);
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
    setOrg(data.organization);
    try {
      const m = await api.get("/meta");
      setMeta(m.data);
    } catch (e) {
      /* ignore */
    }
    setLoading(false);
  }, []);

  /** Superadmin "open workspace": keep the HQ session aside, switch to the tenant token. */
  const impersonate = useCallback(async (data) => {
    const current = localStorage.getItem(TOKEN_KEY);
    resetSetupCache();
    if (current) localStorage.setItem(HQ_TOKEN_KEY, current);
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser({ ...data.user, impersonated_by: "hq" });
    setOrg(data.organization);
    try {
      const m = await api.get("/meta");
      setMeta(m.data);
    } catch (e) {
      /* ignore */
    }
  }, []);

  /** Leave the tenant workspace and restore the HQ session. */
  const exitImpersonation = useCallback(async () => {
    const hq = localStorage.getItem(HQ_TOKEN_KEY);
    resetSetupCache();
    localStorage.removeItem(HQ_TOKEN_KEY);
    if (hq) {
      localStorage.setItem(TOKEN_KEY, hq);
      setToken(hq);
      setLoading(true);
      await refresh();
    } else {
      logout();
    }
  }, [refresh, logout]);

  const value = useMemo(() => ({ token, user, org, meta, loading, login, logout, register, refresh, setOrg, impersonate, exitImpersonation, adoptSession }), [token, user, org, meta, loading, login, logout, register, refresh, impersonate, exitImpersonation, adoptSession]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export function RequireAuth({ children }) {
  const { token, loading, user } = useAuth();
  const loc = useLocation();
  if (loading) return <BootScreen data-testid="loading-state" />;
  if (!token || !user) return <Navigate to="/app/login" state={{ from: loc.pathname + loc.search }} replace />;
  if (!user.organization_id) return <Navigate to="/hq" replace />; // HQ-only account: no tenant workspace
  return children;
}

export function RequireSuperadmin({ children }) {
  const { token, loading, user } = useAuth();
  const loc = useLocation();
  if (loading) return <BootScreen tone="dark" data-testid="loading-state" />;
  if (!token || !user) return <Navigate to="/app/login" state={{ from: loc.pathname + loc.search }} replace />;
  if (!user.is_superadmin) return <Navigate to="/app" replace />;
  return children;
}
